import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Trash2, Mail } from "lucide-react";

const STATUSES = ["new", "contacted", "qualified", "won", "lost"];
const statusColor = {
  new: "bg-vantage/10 text-vantage", contacted: "bg-amber-500/10 text-amber-400",
  qualified: "bg-purple-500/10 text-purple-400", won: "bg-emerald-500/10 text-emerald-400",
  lost: "bg-red-500/10 text-red-400",
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/data/leads").then((r) => setLeads(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.put(`/admin/data/leads/${id}`, { status });
      toast.success("Status updated");
    } catch { toast.error("Failed to update"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/data/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-leads">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-white">Leads</h1>
        <p className="mt-1 text-sm text-slate-400">{leads.length} enquiries from your contact form.</p>
      </div>
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-ink-900">
        {loading ? (
          <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-vantage" /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-slate-400">Name</TableHead>
                <TableHead className="text-slate-400">Contact</TableHead>
                <TableHead className="text-slate-400">Service</TableHead>
                <TableHead className="text-slate-400">Budget</TableHead>
                <TableHead className="text-slate-400">Message</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-right text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.map((l) => (
                <TableRow key={l.id} className="border-white/5" data-testid={`lead-${l.id}`}>
                  <TableCell className="font-medium text-white">{l.name}</TableCell>
                  <TableCell className="text-slate-300">
                    <div className="text-sm">{l.email}</div>
                    {l.phone && <div className="text-xs text-slate-500">{l.phone}</div>}
                  </TableCell>
                  <TableCell className="text-slate-300">{l.service || "—"}</TableCell>
                  <TableCell className="text-slate-300">{l.budget || "—"}</TableCell>
                  <TableCell className="max-w-[220px] text-slate-400"><span className="line-clamp-2 text-sm">{l.message}</span></TableCell>
                  <TableCell>
                    <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                      <SelectTrigger className={`h-8 w-32 border-0 text-xs ${statusColor[l.status] || ""}`} data-testid={`lead-status-${l.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-ink-800 text-white">
                        {STATUSES.map((s) => <SelectItem key={s} value={s} className="text-sm capitalize">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => remove(l.id)} className="rounded-lg p-2 text-slate-400 hover:bg-red-500/10 hover:text-red-400" data-testid={`lead-delete-${l.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
              {leads.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-12 text-center text-slate-500">
                  <Mail className="mx-auto mb-3 h-8 w-8 opacity-40" />No leads yet.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
